// MIDAS V3 — STREAMING ONLY: the bot's raw `[[…]]` shorthand for the chip
// families that srcChipsGrammar (r5c) does not cover
// (MIDAS-STREAM-SHORTHAND-FAMILIES-1, 2026-10-06).
//
// The LLM writes shorthand; the bot's web formatter turns it into chip tags
// only when it builds the FINAL `answer` event (BOT_CAPABILITIES.md §12: "The
// LLM never emits XML — it emits a [[…]] shorthand and the web formatter
// resolves it"). The token deltas carry the raw shorthand "exactly like every
// other chip family" (bot r5 answer 4, MACHINEID_TODO.md 2026-10-04), so these
// codes used to flash as raw text and snap into chips at the end.
//
// A complete code is turned into the tag the bot mints for it, and the final
// path's own rewriteAllRefsToSentinels renders that tag — so the streamed chip
// is the final chip, through the same code:
//
//   [[live:<addr>]]                → <live-ref address>   LIVE chip, grey pip
//                                    (the final adds value= — the read value is
//                                    stamped server-side; mid-stream there is none)
//   [[plc:<addr>]]                 → <plc-ref address>    PLC chip
//   [[tel:<key>:<window>:<count>]] → <tel-ref …>key (window, n=count)</tel-ref>
//   [[tel:<key>:current]]          → <tel-ref …></tel-ref> (final path's label
//                                    fallback; the bot's snapshot label is not
//                                    documented)
//   [[ref:<page>:<collection>]]    → removed: the final formatter strips it into
//                                    the structured `sources` list
//                                    (PLC_CHAIN_GROUND_TRUTH.md III-C.2 / III-C.6)
//   [[list:N]] / [[page:N]]        → not expanded here: rewriteAllRefsToSentinels
//                                    already owns that shorthand (final path too);
//                                    this file only holds back their half tail.
//
// Grammar discipline (as srcChipsGrammar / telqGrammar): case-sensitive, no
// whitespace tolerance around `[[`, `:` or `]]`, bounded fields. The bot's own
// patterns are not published, so every field is the DOCUMENTED shape set and
// never wider. A code that does not match stays plain text — never a chip,
// never removed. Pure — no React, no DOM.

// PLC address — the documented classes: bare bit `I 34.0` / `Q40.6`, timer
// `T 16`, analog `AI 1`, DB `DB10.DBX108.6`, symbolic `DB10."NC".E_611Dready`,
// alarm array `DB2."ALMSG_DB".A7000xx[48]` (PLC_CHAIN_GROUND_TRUTH.md). Words of
// [A-Za-z0-9._"$] joined by single spaces, optionally ending in ONE `[n]`.
const ADDR_WORD = '[A-Za-z0-9._"$]+';
const ADDR = `${ADDR_WORD}(?: ${ADDR_WORD})*(?:\\[\\d{1,4}\\])?`;
const ADDR_MAX = 64;

// <tel-ref> fields: signal_key grammar + `#` sub-key are published
// (BOT_CAPABILITIES §12, 2026-08-22 amendment); window = lowercase token
// (`this_month`, `today`, `current`); count = integer, no leading zero.
const TEL_KEY = "[A-Za-z0-9_]{1,48}(?:#[A-Za-z0-9_-]{1,64})?";
const TEL_WINDOW = "[a-z][a-z0-9_]{0,31}";
const TEL_COUNT = "0|[1-9]\\d{0,8}";
const TEL_SNAPSHOT_WINDOW = "current";

const PLC_SHORTHAND_RE = new RegExp(`\\[\\[(live|plc):(${ADDR})\\]\\]`, "g");
const TEL_SHORTHAND_RE = new RegExp(
  `\\[\\[tel:(${TEL_KEY}):(${TEL_WINDOW})(?::(${TEL_COUNT}))?\\]\\]`,
  "g"
);
// Page + collection (collection names are [A-Za-z0-9_], e.g.
// `sbl500_manual_toc_v2`). Horizontal space before the code goes with it, so
// "strane 42 [[ref:…]]." reads "strane 42." instead of "strane 42 .".
const REF_SHORTHAND_RE = /[ \t]*\[\[ref:\d{1,6}:[A-Za-z0-9_]{1,64}\]\]/g;

// Python html.escape — what the bot applies before minting (quote=True for
// attribute values, quote=False for inner text). readAttr / decodeLabel in
// markdownComponentsV3 decode it exactly once.
const escapeHtml = (value: string, quote: boolean): string => {
  const out = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return quote ? out.replace(/"/g, "&quot;").replace(/'/g, "&#x27;") : out;
};

const mintPlcTag = (family: string, addr: string): string => {
  const tag = family === "live" ? "live-ref" : "plc-ref";
  return `<${tag} address="${escapeHtml(addr, true)}">${escapeHtml(addr, false)}</${tag}>`;
};

const mintTelTag = (key: string, window: string, count: string | undefined): string => {
  const attrs = `signal_key="${escapeHtml(key, true)}" window="${escapeHtml(window, true)}"`;
  if (count === undefined) return `<tel-ref ${attrs}></tel-ref>`;
  const label = `${key} (${window}, n=${count})`;
  return `<tel-ref ${attrs} count="${count}">${escapeHtml(label, false)}</tel-ref>`;
};

/** Complete, documented-shape codes → the tag the bot mints (or nothing, for
 *  `[[ref:…]]`). Anything else is returned untouched. */
export const expandStreamChipShorthand = (text: string): string => {
  if (!text || text.indexOf("[[") === -1) return text;
  return text
    .replace(REF_SHORTHAND_RE, "")
    .replace(PLC_SHORTHAND_RE, (whole, family: string, addr: string) =>
      addr.length <= ADDR_MAX ? mintPlcTag(family, addr) : whole
    )
    .replace(TEL_SHORTHAND_RE, (whole, key: string, window: string, count?: string) => {
      // count is omitted ONLY on the `:current` snapshot form, and only there.
      const isSnapshot = window === TEL_SNAPSHOT_WINDOW;
      if (isSnapshot !== (count === undefined)) return whole;
      return mintTelTag(key, window, count);
    });
};

// ─── Hold back a half-arrived code ───────────────────────────────────
//
// Each family: its keyword and a regex for every strict prefix of a valid
// body + `]]` (so `I 34.0]` is held while its second `]` is in flight).

const PARTIAL_ADDR_RE = new RegExp(
  `^(?:${ADDR_WORD}(?: ${ADDR_WORD})*(?: |\\[\\d{0,4}|\\[\\d{1,4}\\]\\]?|\\])?)?$`
);
const PARTIAL_TEL_RE = new RegExp(
  `^(?:[A-Za-z0-9_]{1,48}(?:#[A-Za-z0-9_-]{0,64})?` +
    `(?::(?:${TEL_WINDOW}(?::\\d{0,9}\\]?|\\])?)?)?)?$`
);
const PARTIAL_REF_RE = /^(?:\d{1,6}(?::(?:[A-Za-z0-9_]{1,64}\]?)?)?)?$/;
const PARTIAL_NUM_RE = /^(?:\d{1,12}\]?)?$/;

interface PartialFamily {
  keyword: string;
  body: RegExp;
  /** Longest body that can still complete (full body + one `]`). */
  max: number;
  /** list/page: the final path's `[[list:N]]` / `[[page:N]]` regexes are `/gi`. */
  anyCase?: boolean;
}

const PARTIAL_FAMILIES: PartialFamily[] = [
  { keyword: "live:", body: PARTIAL_ADDR_RE, max: ADDR_MAX + 1 },
  { keyword: "plc:", body: PARTIAL_ADDR_RE, max: ADDR_MAX + 1 },
  { keyword: "tel:", body: PARTIAL_TEL_RE, max: 48 + 65 + 1 + 32 + 1 + 9 + 1 },
  { keyword: "ref:", body: PARTIAL_REF_RE, max: 6 + 1 + 64 + 1 },
  { keyword: "list:", body: PARTIAL_NUM_RE, max: 13, anyCase: true },
  { keyword: "page:", body: PARTIAL_NUM_RE, max: 13, anyCase: true },
];

/** True when `tail` (from a `[[`, or a lone `[`) can still become a code. */
export const isPartialChipShorthand = (tail: string): boolean => {
  if (tail === "[" || tail === "[[") return true;
  if (!tail.startsWith("[[")) return false;
  const rest = tail.slice(2);
  return PARTIAL_FAMILIES.some(({ keyword, body, max, anyCase }) => {
    const head = anyCase ? rest.toLowerCase() : rest;
    if (head.length <= keyword.length) return keyword.startsWith(head);
    const tailBody = rest.slice(keyword.length);
    return head.startsWith(keyword) && tailBody.length <= max && body.test(tailBody);
  });
};

/**
 * Withhold a trailing half code (`[[li`, `[[plc:DB10."NC".E_6`,
 * `[[tel:part:this_month:103`, `[[ref:42:sbl500_man`) until its `]]` lands.
 * Anchored on the LAST `[[` — not the last `[` — because an alarm-array
 * address carries its own `[n]`. A tail that stops matching is released on
 * the very next character.
 */
export const holdPartialChipShorthandTail = (buffer: string): string => {
  if (!buffer || buffer.indexOf("[") === -1) return buffer;
  const start = buffer.lastIndexOf("[[");
  if (start !== -1 && isPartialChipShorthand(buffer.slice(start))) {
    return buffer.slice(0, start);
  }
  return buffer.endsWith("[") ? buffer.slice(0, -1) : buffer;
};
